import React, {useState, Fragment, useEffect} from 'react'
import PropTypes from 'prop-types'
import DatePicker from "react-datepicker";
import Row from '../../bootstrap/component/Row'
import Column from '../../bootstrap/component/Column'
import {usePrices} from "../PricesContext";
import sdk, {FOLLOW_UP} from '../sdk';
import ContentBoxHeader from "../../layout/component/ContentBoxHeader";
import ContentBoxBody from "../../layout/component/ContentBoxBody";
import * as selector from '../../security/state/auth/selector';
import {SALES_NOTE_WIDGET_CONTAINER} from "../../process/ui-definition/process-ui";
import {ROLE_CLINIC_SALES} from "../../security/state/auth/const";

const SalesNotes = ({customerId, isReadOnly, state}) => {
    const isVisible = selector.userHasWidgetInVisibleList(state, SALES_NOTE_WIDGET_CONTAINER);
    const hasRole = selector.selectHasRole(state, ROLE_CLINIC_SALES);

    if (!hasRole && isVisible) {
        isReadOnly = true;
    }

    if (!hasRole && !isVisible) {
        return '';
    }

    const {treatmentPrices} = usePrices();

    const [persistedNote, setPersistedNote] = useState(null);
    const [note, setNote] = useState({
        followUpType: '',
        promisedPrice: '',
        treatmentPlanDeadline: null,
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);

    const treatmentDeadlineIsEnabled = [FOLLOW_UP.IN_CLINIC, FOLLOW_UP.EXPERT_CALL].includes(note.followUpType);

    useEffect(() => {
        (async function () {
            try {
                let data = await sdk.findNote(customerId);
                setPersistedNote(data);
                setNote(data);
            } catch (e) {
                setError(e.message);
            }
            setLoading(false);
        })();
    }, [customerId]);

    const enableEditing = () => {
        setEditing(true)
    };

    const cancelEditing = () => {
        setEditing(false);
        setError(null);
        setNote(persistedNote || {
            followUpType: '',
            promisedPrice: treatmentPrices.medium,
            treatmentPlanDeadline: null,
        });
    };

    const saveChanges = async () => {
        setLoading(true);
        setError(null);
        try {
            await sdk.saveNote(customerId, note);
            setPersistedNote(note);
            setEditing(false)
        } catch (e) {
            setError(e.message || JSON.stringify(e));
        }
        setLoading(false);
    };

    function setPromisedPrice(promisedPrice) {
        setError(null);
        return setNote({...note, promisedPrice});
    }

    function setFollowUp(followUpType) {
        setError(null);
        if ([FOLLOW_UP.NO_PITCH, FOLLOW_UP.NO_FOLLOW_UP].includes(followUpType)) {
            return setNote({
                ...note,
                followUpType,
                treatmentPlanDeadline: null,
            });
        }

        return setNote({...note, followUpType});
    }

    function setTreatmentPlanDeadline(date) {
        setError(null);
        return setNote({
            ...note,
            treatmentPlanDeadline: date
        });
    }

    return <Row>
        <Column layer="lg" size={12}>
            <div className="content-box">
                <ContentBoxHeader>
                    <Row>
                        <Column layer={'md'} size={8}>
                            <h1>Sales Notes</h1>
                        </Column>
                        <Column layer={'md'} size={4}>
                            <div style={{textAlign: 'right'}}>
                                {(!editing && !isReadOnly) &&
                                <button onClick={enableEditing} className="btn btn-primary">
                                    <i className="fa fa-edit"/>
                                </button>}
                                {editing && <Fragment>
                                    <button onClick={cancelEditing}
                                            className="btn"
                                            disabled={loading}
                                            style={{marginLeft: '5px'}}
                                    >Cancel
                                    </button>
                                    <button onClick={saveChanges}
                                            className="btn btn-primary"
                                            disabled={loading}
                                            type="submit"
                                            style={{marginLeft: '5px'}}
                                    >Save
                                    </button>
                                </Fragment>}
                            </div>
                        </Column>
                    </Row>
                </ContentBoxHeader>
                <ContentBoxBody>
                    {error && <Row>
                        <Column layer={'md'} size={12}>
                            <div className="alert alert-danger" role="alert">
                                <i className="fa fa-exclamation-circle"/>
                                {error}
                            </div>
                        </Column>
                    </Row>}
                    <Row>
                        <Column layer={'md'} size={6}>
                            <form>
                                <div className="form-group">
                                    <label htmlFor="sales_promise_followup_type">Follow-up type</label>
                                    <select name="followup"
                                            id="sales_promise_followup_type"
                                            className="form-control"
                                            disabled={!editing || loading}
                                            value={note.followUpType}
                                            onChange={(e) => setFollowUp(e.target.value)}
                                    >
                                        <option key="empty" value=""></option>
                                        <option key={FOLLOW_UP.IN_CLINIC} value={FOLLOW_UP.IN_CLINIC}>In-Clinic
                                            Follow-Up (ICSTP1)
                                        </option>
                                        <option key={FOLLOW_UP.EXPERT_CALL} value={FOLLOW_UP.EXPERT_CALL}>Expert
                                            Call Follow-Up (ICSTP2)
                                        </option>
                                        <option key={FOLLOW_UP.NO_FOLLOW_UP} value={FOLLOW_UP.NO_FOLLOW_UP}>No
                                            Follow-Up booked (ICSTP3)
                                        </option>
                                        <option key={FOLLOW_UP.NO_PITCH} value={FOLLOW_UP.NO_PITCH}>No Pitch and
                                            Follow-Up (ICSTP4)
                                        </option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="sales_promise_price">Promised price</label>
                                    <div className="input-group">
                                        <span className="input-group-addon">&euro;</span>
                                        <select name="promisedPrice"
                                                id="sales_promise_price"
                                                className="form-control"
                                                disabled={!editing || loading}
                                                value={note.promisedPrice}
                                                onChange={(e) => setPromisedPrice(e.target.value)}
                                        >
                                            <option key="empty" value=""></option>
                                            <option key="zero" value="0">0.0</option>
                                            {Object.entries(treatmentPrices).map(([value, label]) => {
                                                return <option key={value}
                                                               value={label}>{(label / 100).toFixed(2)}</option>;
                                            })}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="sales_promise_deadline">Treatment plan deadline</label>
                                    <div className="input-group">
                                        <DatePicker
                                            id="sales_promise_deadline"
                                            className="form-control"
                                            autoComplete="off"
                                            selected={note.treatmentPlanDeadline}
                                            minDate={note.treatmentPlanDeadline}
                                            readOnly={!editing}
                                            disabled={!editing || loading || !treatmentDeadlineIsEnabled}
                                            showTimeSelect={false}
                                            dateFormat={'DD.MM.YYYY'}
                                            onChange={setTreatmentPlanDeadline}
                                        />
                                    </div>
                                </div>
                            </form>
                        </Column>
                    </Row>
                </ContentBoxBody>
            </div>
        </Column>
    </Row>;
}

SalesNotes.propTypes = {
    customerId: PropTypes.string.isRequired,
    isReadOnly: PropTypes.bool,
}

export default SalesNotes



// WEBPACK FOOTER //
// ./src/modules/sales/component/SalesNotes.js